import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save,
  Truck,
  Package,
  DollarSign,
  Clock,
  Edit,
  Check,
  CheckCircle,
  Gift
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// Default shipping options
const DEFAULT_SHIPPING_OPTIONS = [
  { id: 'standard', name: 'Standard Shipping', price: 5.99, days: '5-7 business days', enabled: true },
  { id: 'express', name: 'Express Shipping', price: 12.99, days: '2-3 business days', enabled: true },
  { id: 'overnight', name: 'Overnight Shipping', price: 24.99, days: '1 business day', enabled: true },
  { id: 'pickup', name: 'Local Pickup', price: 0, days: 'Pick up in Kailua-Kona', enabled: true }
];

const AdminShippingSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  
  // Shipping options state
  const [shippingOptions, setShippingOptions] = useState(DEFAULT_SHIPPING_OPTIONS);
  
  // Products state
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  
  // Free shipping settings
  const [freeShippingProducts, setFreeShippingProducts] = useState([]);
  const [freeShippingCollections, setFreeShippingCollections] = useState([]);
  const [productShippingMethods, setProductShippingMethods] = useState({}); // {productId: ['standard', 'express']}
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(50);
  const [enableFreeShippingThreshold, setEnableFreeShippingThreshold] = useState(false);
  const [allProductsFreeShipping, setAllProductsFreeShipping] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch shipping settings
      const settingsRes = await fetch(`${API}/admin/shipping-settings`);
      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        if (settings.shipping_options && settings.shipping_options.length > 0) {
          setShippingOptions(settings.shipping_options);
        }
        if (settings.free_shipping_products) {
          setFreeShippingProducts(settings.free_shipping_products);
        }
        if (settings.free_shipping_collections) {
          setFreeShippingCollections(settings.free_shipping_collections);
        }
        if (settings.product_shipping_methods) {
          setProductShippingMethods(settings.product_shipping_methods);
        }
        if (settings.free_shipping_threshold !== undefined) {
          setFreeShippingThreshold(settings.free_shipping_threshold);
        }
        if (settings.enable_free_shipping_threshold !== undefined) {
          setEnableFreeShippingThreshold(settings.enable_free_shipping_threshold);
        }
        if (settings.all_products_free_shipping !== undefined) {
          setAllProductsFreeShipping(settings.all_products_free_shipping);
        }
      }
      
      // Fetch products
      const productsRes = await fetch(`${API}/products`);
      if (productsRes.ok) {
        const productsData = await productsRes.json();
        setProducts(productsData);
        
        // Extract unique collections/categories
        const uniqueCollections = [...new Set(productsData.map(p => p.category).filter(Boolean))];
        setCollections(uniqueCollections);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/shipping-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shipping_options: shippingOptions,
          free_shipping_products: freeShippingProducts,
          free_shipping_collections: freeShippingCollections,
          product_shipping_methods: productShippingMethods,
          free_shipping_threshold: freeShippingThreshold,
          enable_free_shipping_threshold: enableFreeShippingThreshold,
          all_products_free_shipping: allProductsFreeShipping
        })
      });
      
      if (response.ok) {
        alert('Shipping settings saved successfully!');
      } else {
        alert('Failed to save settings');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const addShippingOption = () => {
    const newOption = {
      id: `shipping-${Date.now()}`,
      name: 'New Shipping Option',
      price: 0,
      days: 'X business days',
      enabled: true
    };
    setShippingOptions([...shippingOptions, newOption]);
    setEditingOption(newOption.id);
  };

  const updateShippingOption = (id, field, value) => {
    setShippingOptions(options => 
      options.map(opt => opt.id === id ? { ...opt, [field]: value } : opt)
    );
  };

  const removeShippingOption = (id) => {
    if (!window.confirm('Are you sure you want to remove this shipping option?')) return;
    setShippingOptions(options => options.filter(opt => opt.id !== id));
  };

  const toggleFreeShipping = (productId) => {
    setFreeShippingProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      }
      return [...prev, productId];
    });
  };

  const toggleCollectionFreeShipping = (collection) => {
    setFreeShippingCollections(prev => {
      if (prev.includes(collection)) {
        return prev.filter(c => c !== collection);
      }
      return [...prev, collection];
    });
  };

  const setAllFreeShipping = (enabled) => {
    setAllProductsFreeShipping(enabled);
    if (enabled) {
      // Add all product IDs to free shipping list
      setFreeShippingProducts(products.map(p => p._id));
    } else {
      setFreeShippingProducts([]);
    }
  };

  const updateProductShippingMethod = (productId, method) => {
    setProductShippingMethods(prev => ({
      ...prev,
      [productId]: method
    }));
  };

  const isProductFreeShipping = (product) => {
    if (allProductsFreeShipping) return true;
    if (freeShippingProducts.includes(product._id)) return true;
    if (product.category && freeShippingCollections.includes(product.category)) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Shipping Settings
              </h1>
              <p className="text-sm text-gray-500">Configure shipping options and pricing</p>
            </div>
          </div>
          <Button 
            onClick={saveSettings}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        
        {/* Quick Actions - All Products Free Shipping */}
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <Gift className="w-5 h-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-green-200">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  allProductsFreeShipping ? 'bg-green-500' : 'bg-gray-200'
                }`}>
                  {allProductsFreeShipping ? (
                    <CheckCircle className="w-6 h-6 text-white" />
                  ) : (
                    <Truck className="w-6 h-6 text-gray-500" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Make All Products Free Shipping</p>
                  <p className="text-sm text-gray-500">Enable free standard shipping for all products</p>
                </div>
              </div>
              <Switch
                checked={allProductsFreeShipping}
                onCheckedChange={setAllFreeShipping}
              />
            </div>
            {allProductsFreeShipping && (
              <div className="mt-3 p-3 bg-green-100 rounded-lg text-green-800 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                All products now have FREE standard shipping!
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shipping Options */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Shipping Options & Pricing
              </CardTitle>
              <CardDescription>Configure available shipping methods and their costs</CardDescription>
            </div>
            <Button onClick={addShippingOption} className="bg-green-500 hover:bg-green-600">
              <Plus className="w-4 h-4 mr-1" /> Add Option
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {shippingOptions.map((option) => (
                <div 
                  key={option.id} 
                  className={`p-4 rounded-xl border-2 transition-all ${
                    option.enabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
                >
                  {editingOption === option.id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Option Name</Label>
                          <Input 
                            value={option.name}
                            onChange={(e) => updateShippingOption(option.id, 'name', e.target.value)}
                            placeholder="e.g., Standard Shipping"
                          />
                        </div>
                        <div>
                          <Label>Price ($)</Label>
                          <Input 
                            type="number"
                            step="0.01"
                            min="0"
                            value={option.price}
                            onChange={(e) => updateShippingOption(option.id, 'price', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Delivery Time</Label>
                        <Input 
                          value={option.days}
                          onChange={(e) => updateShippingOption(option.id, 'days', e.target.value)}
                          placeholder="e.g., 5-7 business days"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setEditingOption(null)}
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={option.enabled}
                            onCheckedChange={(checked) => updateShippingOption(option.id, 'enabled', checked)}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-gray-500" />
                            <span className="font-semibold text-gray-900">{option.name}</span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {option.days}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xl font-bold ${option.price === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                          {option.price === 0 ? 'FREE' : `$${option.price.toFixed(2)}`}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setEditingOption(option.id)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeShippingOption(option.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Free Shipping by Collection/Category */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-600" />
              Free Shipping by Category
            </CardTitle>
            <CardDescription>Enable free shipping for entire product categories</CardDescription>
          </CardHeader>
          <CardContent>
            {collections.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No categories found</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {collections.map((collection) => {
                  const hasFreeship = freeShippingCollections.includes(collection);
                  const productCount = products.filter(p => p.category === collection).length;
                  return (
                    <div
                      key={collection}
                      onClick={() => toggleCollectionFreeShipping(collection)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        hasFreeship 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{collection}</p>
                          <p className="text-sm text-gray-500">{productCount} products</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          hasFreeship ? 'bg-purple-500' : 'border-2 border-gray-300'
                        }`}>
                          {hasFreeship && <Check className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                      {hasFreeship && (
                        <div className="mt-2 text-xs text-purple-600 font-medium">
                          ✓ Free Shipping Enabled
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Free Shipping Threshold */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Free Shipping Threshold
            </CardTitle>
            <CardDescription>Offer free shipping for orders above a certain amount</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <Switch
                checked={enableFreeShippingThreshold}
                onCheckedChange={setEnableFreeShippingThreshold}
              />
              <div className="flex-1">
                <p className="font-medium text-gray-900">
                  Enable free shipping for orders over:
                </p>
                <p className="text-sm text-gray-500">
                  Customers will get free standard shipping when their order exceeds this amount
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <Input 
                  type="number"
                  min="0"
                  value={freeShippingThreshold}
                  onChange={(e) => setFreeShippingThreshold(parseFloat(e.target.value) || 0)}
                  className="w-24"
                  disabled={!enableFreeShippingThreshold}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Product-Specific Shipping Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-amber-600" />
              Product Shipping Settings
            </CardTitle>
            <CardDescription>Configure shipping for individual products</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {products.map((product) => {
                const hasFreeShipping = isProductFreeShipping(product);
                const selectedMethod = productShippingMethods[product._id] || 'all';
                
                return (
                  <div 
                    key={product._id}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      hasFreeShipping 
                        ? 'border-green-300 bg-green-50' 
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {product.image || product.image_url ? (
                          <img 
                            src={product.image || product.image_url} 
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Package className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span>${product.price?.toFixed(2) || '0.00'}</span>
                            {product.category && (
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{product.category}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Shipping Method Selector */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-gray-500">Shipping:</Label>
                          <Select 
                            value={selectedMethod} 
                            onValueChange={(value) => updateProductShippingMethod(product._id, value)}
                          >
                            <SelectTrigger className="w-36">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Methods</SelectItem>
                              {shippingOptions.filter(o => o.enabled).map(opt => (
                                <SelectItem key={opt.id} value={opt.id}>
                                  {opt.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Free Shipping Toggle */}
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                            hasFreeShipping 
                              ? 'bg-green-200 text-green-800' 
                              : 'bg-gray-200 text-gray-600'
                          }`}>
                            {hasFreeShipping ? '✓ Free' : 'Paid'}
                          </span>
                          <Switch
                            checked={freeShippingProducts.includes(product._id)}
                            onCheckedChange={() => toggleFreeShipping(product._id)}
                            disabled={allProductsFreeShipping}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <h4 className="font-semibold text-amber-900 mb-2">How Shipping Works:</h4>
            <ul className="text-sm text-amber-800 space-y-1">
              <li>• <strong>All Products Free</strong> - Toggle to enable free shipping for everything</li>
              <li>• <strong>By Category</strong> - Click a category to enable free shipping for all products in it</li>
              <li>• <strong>By Product</strong> - Toggle individual products for free shipping</li>
              <li>• <strong>Shipping Method</strong> - Restrict which shipping methods are available per product</li>
              <li>• <strong>Threshold</strong> - Free shipping when cart total exceeds the set amount</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminShippingSettings;
